import { memo, useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { ACTION_COL_WIDTH, findScrollParent, shouldShowFloatingHeader } from './stickyUtils';
import type { ColumnConfig } from './types';

interface FloatingTableHeaderProps {
  /** The `.editable-table-scroll` element — the horizontal scroll container. */
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  /** The real header `<tr>` — measured for position, height, and scroll sync. */
  headerRowRef: RefObject<HTMLTableRowElement | null>;
  /** Post-stretch columns — drives the clone's `<colgroup>` widths, IDENTICAL to
   *  the real table's colgroup so the two lay out pixel-for-pixel the same. */
  renderColumns: ColumnConfig[];
  hasLeadingCol: boolean;
  /** The shared header-row render (closes over the real handlers). Called with
   *  `true` here so the clone is a full, interactive copy of the real header. */
  renderHeaderRow: (isClone: boolean) => ReactNode;
  /** Pixels to pin below the snap line — the height of the sticky toolbar so the
   *  header clone stacks directly under it (0 when there's no sticky toolbar). */
  stickyOffset?: number;
}

/**
 * A fixed-position, fully-interactive clone of the grid's header row. When the
 * real header scrolls above the top of the app content scroll area, this pins
 * there so the header stays visible + usable while rows scroll underneath; it
 * hides again once the grid body scrolls past.
 *
 * Why a clone rather than CSS `position: sticky`: the table's horizontal scroll
 * container (`.editable-table-scroll`, `overflow-x: auto`) is a scroll container
 * on both axes and traps a sticky header inside itself, so it can never stick to
 * the window. This clone lives OUTSIDE that container (a child of
 * `.editable-table-container`) and is positioned/scrolled imperatively.
 *
 * It renders the SAME `renderHeaderRow` as the real header (closing over the
 * real sort/filter/resize handlers, which target the real table via
 * `headerRowRef`), so sort/filter/resize all work from the pinned header.
 *
 * **Column widths:** the clone table is made IDENTICAL to the real table in
 * every width-relevant property — same `<colgroup>` widths (`renderColumns`),
 * `table-layout: fixed` and `width: 100%` (forced inline so it can never fall
 * back to content-based `auto` sizing) — so the two resolve to the same column
 * tracks. During a live resize, `startResize` (useColumnWidths) mutates BOTH the
 * real and clone `<col data-col-key>` elements, keeping them in lockstep; on
 * release the shared `renderColumns` state re-renders both to the final width.
 *
 * Positioning/scroll follow the `StickyScrollbar` pattern: measure the scroll
 * container via `getBoundingClientRect`, re-follow on capture-phase `document`
 * scroll + window resize + `ResizeObserver`, all rAF-throttled.
 */
function FloatingTableHeaderImpl({
  scrollContainerRef,
  headerRowRef,
  renderColumns,
  hasLeadingCol,
  renderHeaderRow,
  stickyOffset = 0,
}: FloatingTableHeaderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const scroll = scrollContainerRef.current;
    const root = rootRef.current;
    if (!scroll || !root) return;

    scrollParentRef.current = null;

    let raf = 0;
    const measure = () => {
      raf = 0;
      const header = headerRowRef.current;
      if (!header) return;

      // Resolve the scroll viewport lazily: `findScrollParent` skips
      // declared-but-non-clipping containers (an auto-height, overflow-y:auto
      // TabsContent), so it can return null until the grid grows tall enough to
      // make a real scroller overflow. Re-resolve until we latch one.
      if (!scrollParentRef.current) {
        scrollParentRef.current = findScrollParent(scroll);
      }

      const sc = scroll.getBoundingClientRect();
      const hd = header.getBoundingClientRect();
      // Pin below the sticky toolbar: snap line + toolbar height.
      const snapTop =
        (scrollParentRef.current ? scrollParentRef.current.getBoundingClientRect().top : 0) +
        stickyOffset;
      const height = hd.height;

      if (!shouldShowFloatingHeader(hd.top, sc.bottom, snapTop, height)) {
        root.style.display = 'none';
        return;
      }

      root.style.top = `${snapTop}px`;
      root.style.left = `${sc.left}px`;
      root.style.width = `${sc.width}px`;
      root.style.height = `${height}px`;
      root.style.display = '';
      // Mirror the horizontal scroll. `scrollLeft` on the overflow:hidden root
      // re-pins any sticky-left columns exactly as in the real grid.
      root.scrollLeft = scroll.scrollLeft;
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    measure();

    const ro = new ResizeObserver(schedule);
    ro.observe(scroll);
    const header = headerRowRef.current;
    if (header) ro.observe(header);

    scroll.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    // Capture phase catches scrolls in ancestors (app layout container) where
    // scroll events don't bubble to window.
    document.addEventListener('scroll', schedule, { passive: true, capture: true });

    return () => {
      ro.disconnect();
      scroll.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('scroll', schedule, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollContainerRef, headerRowRef, stickyOffset]);

  return (
    <div
      ref={rootRef}
      className="editable-table-floating-header"
      style={{ display: 'none' }}
    >
      {/* Identical to the real table's width model: fixed layout + 100% width +
          the same colgroup tracks → the columns can't diverge. Forced inline so
          it never falls back to content-based `auto` sizing. */}
      <table className="editable-table--fixed" style={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {hasLeadingCol && <col style={{ width: ACTION_COL_WIDTH }} />}
          {renderColumns.map((col) => (
            <col key={col.data} data-col-key={col.data} style={{ width: `${col.width}px` }} />
          ))}
        </colgroup>
        <thead>{renderHeaderRow(true)}</thead>
      </table>
    </div>
  );
}

export const FloatingTableHeader = memo(FloatingTableHeaderImpl);
